"use client";

import { enUS } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { DayPicker } from "react-day-picker";
import { buttonVariants } from "../button";
import { cn } from "../lib/utils";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "../select2";
import type { CalendarProps } from "./types";
import { genMonths, genYears } from "./utils";

function Calendar({
	className,
	classNames,
	showOutsideDays = true,
	yearRange = 50,
	...props
}: CalendarProps & { yearRange?: number }) {
	const MONTHS = React.useMemo(() => {
		let locale = enUS;
		const { options, localize, formatLong } = props.locale || {};
		if (options && localize && formatLong) {
			locale = {
				...enUS,
				options,
				localize,
				formatLong,
			};
		}
		return genMonths(locale);
	}, [props.locale]);

	const YEARS = React.useMemo(() => genYears(yearRange), [yearRange]);

	const disableLeftNavigation = React.useCallback(() => {
		const today = new Date();
		const startDate = new Date(today.getFullYear() - yearRange, 0, 1);
		if (props.month) {
			return (
				props.month.getMonth() === startDate.getMonth() &&
				props.month.getFullYear() === startDate.getFullYear()
			);
		}
		return false;
	}, [yearRange, props.month]);

	const disableRightNavigation = React.useCallback(() => {
		const today = new Date();
		const endDate = new Date(today.getFullYear() + yearRange, 11, 31);
		if (props.month) {
			return (
				props.month.getMonth() === endDate.getMonth() &&
				props.month.getFullYear() === endDate.getFullYear()
			);
		}
		return false;
	}, [yearRange, props.month]);

	return (
		<DayPicker
			showOutsideDays={showOutsideDays}
			className={cn("p-3", className)}
			classNames={{
				months:
					"flex flex-col sm:flex-row space-y-4 sm:space-y-0 justify-center",
				month: "flex flex-col items-center space-y-4",
				month_caption: "flex justify-center pt-1 relative items-center",
				caption_label: "text-sm font-medium",
				nav: "space-x-1 flex items-center",
				button_previous: cn(
					buttonVariants({ intent: "outline" }),
					"h-6 w-6 bg-transparent p-0 opacity-50 hover:opacity-100 absolute left-3 top-5",
					disableLeftNavigation() && "pointer-events-none",
				),
				button_next: cn(
					buttonVariants({ intent: "outline" }),
					"h-6 w-6 bg-transparent p-0 opacity-50 hover:opacity-100 absolute right-3 top-5",
					disableRightNavigation() && "pointer-events-none",
				),
				month_grid: "w-full border-collapse space-y-1",
				weekdays: cn("flex", props.showWeekNumber && "justify-end"),
				weekday:
					"text-text-body-muted rounded-md w-9 font-normal text-[0.8rem]",
				week: "flex w-full mt-2",
				day: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-white/50 [&:has([aria-selected])]:bg-white first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 rounded-1",
				day_button: cn(
					buttonVariants({ intent: "ghost" }),
					"h-9 w-9 p-0 font-normal aria-selected:opacity-100 rounded-l-md rounded-r-md",
				),
				range_end: "day-range-end",
				selected: "bg-white text-text-body-inverted rounded-l-md rounded-r-md",
				today: "bg-surface rounded-md",
				outside:
					"day-outside text-text-body-muted opacity-50 aria-selected:bg-accent/50 aria-selected:text-text-body-muted aria-selected:opacity-30",
				disabled: "text-text-body-muted opacity-50",
				range_middle:
					"aria-selected:bg-white aria-selected:text-accent-foreground",
				hidden: "invisible",
				...classNames,
			}}
			components={{
				Chevron: ({ ...props }) =>
					props.orientation === "left" ? (
						<ChevronLeft className="h-4 w-4" />
					) : (
						<ChevronRight className="h-4 w-4" />
					),
				MonthCaption: ({ calendarMonth }) => {
					return (
						<div className="inline-flex gap-2">
							<Select
								defaultValue={calendarMonth.date.getMonth().toString()}
								onValueChange={(value) => {
									const newDate = new Date(calendarMonth.date);
									newDate.setMonth(Number.parseInt(value, 10));
									props.onMonthChange?.(newDate);
								}}
							>
								<SelectTrigger className="w-fit gap-1 border-none p-0 focus:bg-accent focus:text-accent-foreground">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{MONTHS.map((month) => (
										<SelectItem
											key={month.value}
											value={month.value.toString()}
										>
											{month.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
							<Select
								defaultValue={calendarMonth.date.getFullYear().toString()}
								onValueChange={(value) => {
									const newDate = new Date(calendarMonth.date);
									newDate.setFullYear(Number.parseInt(value, 10));
									props.onMonthChange?.(newDate);
								}}
							>
								<SelectTrigger className="w-fit gap-1 border-none p-0 focus:bg-accent focus:text-accent-foreground">
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									{YEARS.map((year) => (
										<SelectItem key={year.value} value={year.value.toString()}>
											{year.label}
										</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
					);
				},
			}}
			{...props}
		/>
	);
}
Calendar.displayName = "Calendar";

export { Calendar };
